import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Eye, EyeOff, X, Save } from "lucide-react";

const empty = {
    titulo: "",
    slug: "",
    extracto: "",
    contenido: "",
    imagen_url: "",
    autor: "Tenería",
    publicado: true,
};

function PostForm({ initial, onSave, onCancel }) {
    const [form, setForm] = useState(initial || empty);
    const [saving, setSaving] = useState(false);
    const upd = (k) => (e) =>
        setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.titulo.trim() || !form.contenido.trim()) {
            toast.error("Título y contenido son obligatorios");
            return;
        }
        setSaving(true);
        try {
            await onSave({
                ...form,
                slug: form.slug.trim() || undefined,
                extracto: form.extracto || "",
                imagen_url: form.imagen_url || null,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="border border-gold/30 bg-bg-alt/50 p-8 space-y-6"
            data-testid="blog-post-form"
        >
            <div className="flex items-center justify-between">
                <h2 className="font-display italic text-2xl text-gold">
                    {initial ? "Editar entrada" : "Nueva entrada"}
                </h2>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-stone hover:text-ivory"
                    aria-label="Cancelar"
                >
                    <X size={18} />
                </button>
            </div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="md:col-span-2">
                    <label className="line-label">Título *</label>
                    <input
                        value={form.titulo}
                        onChange={upd("titulo")}
                        className="input-line"
                        data-testid="blog-titulo"
                        required
                    />
                </div>
                <div>
                    <label className="line-label">Slug (URL)</label>
                    <input
                        value={form.slug}
                        onChange={upd("slug")}
                        className="input-line"
                        placeholder="auto desde título"
                        data-testid="blog-slug"
                    />
                </div>
                <div>
                    <label className="line-label">Autor</label>
                    <input
                        value={form.autor}
                        onChange={upd("autor")}
                        className="input-line"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="line-label">Imagen (URL)</label>
                    <input
                        value={form.imagen_url}
                        onChange={upd("imagen_url")}
                        className="input-line"
                        placeholder="https://..."
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="line-label">Extracto</label>
                    <textarea
                        rows={2}
                        value={form.extracto}
                        onChange={upd("extracto")}
                        className="input-line resize-none"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="line-label">Contenido *</label>
                    <textarea
                        rows={10}
                        value={form.contenido}
                        onChange={upd("contenido")}
                        className="input-line resize-y"
                        data-testid="blog-contenido"
                        required
                    />
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                    <input
                        type="checkbox"
                        id="publicado"
                        checked={form.publicado}
                        onChange={upd("publicado")}
                        className="accent-gold w-4 h-4"
                    />
                    <label htmlFor="publicado" className="font-body text-sm text-ivory">
                        Publicado (visible en la web)
                    </label>
                </div>
                <div className="md:col-span-2 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn-ghost"
                        data-cursor="hover"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="btn-solid-alt disabled:opacity-50"
                        data-testid="blog-save"
                        data-cursor="hover"
                    >
                        <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>
            </div>
        </form>
    );
}

export default function CrmBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | post object

    const load = () => {
        setLoading(true);
        api.get("/blog")
            .then((r) => setPosts(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const create = async (data) => {
        try {
            await api.post("/blog", data);
            toast.success("Entrada creada");
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const update = async (data) => {
        try {
            await api.patch(`/blog/${editing.id}`, data);
            toast.success("Entrada actualizada");
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar esta entrada?")) return;
        try {
            await api.delete(`/blog/${id}`);
            toast.success("Eliminada");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const togglePublicado = async (post) => {
        try {
            await api.patch(`/blog/${post.id}`, { publicado: !post.publicado });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="space-y-10">
            <header className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold mb-3">
                        Blog · Diario
                    </p>
                    <h1 className="font-display text-5xl text-ivory font-light">
                        Noticias y <em className="italic text-gold">novedades</em>
                    </h1>
                </div>
                {!editing && (
                    <button
                        onClick={() => setEditing("new")}
                        className="btn-solid-alt"
                        data-testid="blog-new-btn"
                        data-cursor="hover"
                    >
                        <Plus size={14} /> Nueva entrada
                    </button>
                )}
            </header>

            {editing === "new" && (
                <PostForm onSave={create} onCancel={() => setEditing(null)} />
            )}
            {editing && editing !== "new" && (
                <PostForm
                    initial={editing}
                    onSave={update}
                    onCancel={() => setEditing(null)}
                />
            )}

            {!editing && (
                <>
                    {loading ? (
                        <p className="text-stone italic font-editorial">Cargando...</p>
                    ) : posts.length === 0 ? (
                        <p className="text-stone italic font-editorial">
                            Aún no hay entradas. Crea la primera con "Nueva entrada".
                        </p>
                    ) : (
                        <div className="border border-gold/20 bg-bg-alt/30 divide-y divide-gold/10">
                            {posts.map((p) => (
                                <div
                                    key={p.id}
                                    className="px-5 py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
                                    data-testid="blog-row"
                                >
                                    <div className="md:col-span-6">
                                        <p className="font-display italic text-xl text-ivory">
                                            {p.titulo}
                                        </p>
                                        <p className="font-body text-xs text-stone mt-1">
                                            /{p.slug} · {p.autor}
                                        </p>
                                    </div>
                                    <div className="md:col-span-2 font-body text-sm text-stone">
                                        {p.fecha_pub
                                            ? new Date(p.fecha_pub).toLocaleDateString("es-ES")
                                            : "—"}
                                    </div>
                                    <div className="md:col-span-2">
                                        <button
                                            onClick={() => togglePublicado(p)}
                                            className={`inline-flex items-center gap-2 px-3 py-1 border text-xs uppercase tracking-[0.15em] font-body ${
                                                p.publicado
                                                    ? "border-gold/50 text-gold"
                                                    : "border-stone/40 text-stone"
                                            }`}
                                            data-cursor="hover"
                                        >
                                            {p.publicado ? (
                                                <>
                                                    <Eye size={12} /> Público
                                                </>
                                            ) : (
                                                <>
                                                    <EyeOff size={12} /> Borrador
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <div className="md:col-span-2 flex items-center justify-end gap-3">
                                        <button
                                            onClick={() => setEditing(p)}
                                            className="text-stone hover:text-gold"
                                            aria-label="Editar"
                                            data-cursor="hover"
                                        >
                                            <Edit size={16} />
                                        </button>
                                        <button
                                            onClick={() => remove(p.id)}
                                            className="text-stone hover:text-red-400"
                                            aria-label="Eliminar"
                                            data-testid="blog-delete"
                                            data-cursor="hover"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
